import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import packageService from "./packageService";

// Initial state
const initialState = {
  packages: [],
  package: null,
  isError: false,
  isSuccess: false,
  isLoading: false,
  message: "",
};

// Create a package
export const createPackage = createAsyncThunk(
  "package/create",
  async (packageData, thunkAPI) => {
    try {
      return await packageService.createPackage(packageData);
    } catch (error) {
      const message =
        (error.response && error.response.data && error.response.data.message) ||
        error.message ||
        error.toString();
      return thunkAPI.rejectWithValue(message);
    }
  }
);

// Get all packages
export const getPackages = createAsyncThunk(
  "package/getAll",
  async (_, thunkAPI) => {
    try {
      return await packageService.getPackages();
    } catch (error) {
      const message =
        (error.response && error.response.data && error.response.data.message) ||
        error.message ||
        error.toString();
      return thunkAPI.rejectWithValue(message);
    }
  }
);

// Get a package by ID
export const getPackage = createAsyncThunk(
  "package/get",
  async (id, thunkAPI) => {
    try {
      return await packageService.getPackage(id);
    } catch (error) {
      const message =
        (error.response && error.response.data && error.response.data.message) ||
        error.message ||
        error.toString();
      return thunkAPI.rejectWithValue(message);
    }
  }
);

// Update a package
export const updatePackage = createAsyncThunk(
  "package/update",
  async ({ id, packageData }, thunkAPI) => {
    try {
      return await packageService.updatePackage(id, packageData);
    } catch (error) {
      const message =
        (error.response && error.response.data && error.response.data.message) ||
        error.message ||
        error.toString();
      return thunkAPI.rejectWithValue(message);
    }
  }
);

// Delete a package
export const deletePackage = createAsyncThunk(
  "package/delete",
  async (id, thunkAPI) => {
    try {
      return await packageService.deletePackage(id);
    } catch (error) {
      const message =
        (error.response && error.response.data && error.response.data.message) ||
        error.message ||
        error.toString();
      return thunkAPI.rejectWithValue(message);
    }
  }
);

// Toggle package featured status
export const togglePackageFeaturedStatus = createAsyncThunk(
  "package/toggleFeatured",
  async (id, thunkAPI) => {
    try {
      return await packageService.togglePackageFeaturedStatus(id);
    } catch (error) {
      const message =
        (error.response && error.response.data && error.response.data.message) ||
        error.message ||
        error.toString();
      return thunkAPI.rejectWithValue(message);
    }
  }
);

// Package slice
export const packageSlice = createSlice({
  name: "package",
  initialState,
  reducers: {
    resetState: (state) => {
      state.isLoading = false;
      state.isSuccess = false;
      state.isError = false;
      state.message = "";
      state.package = null;
      state.packages = [];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(createPackage.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(createPackage.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.packages.push(action.payload);
      })
      .addCase(createPackage.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload;
      })
      .addCase(getPackages.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getPackages.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.packages = action.payload;
      })
      .addCase(getPackages.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload;
      })
      .addCase(getPackage.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getPackage.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.package = action.payload;
      })
      .addCase(getPackage.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload;
      })
      .addCase(updatePackage.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(updatePackage.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.package = action.payload;
      })
      .addCase(updatePackage.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload;
      })
      .addCase(deletePackage.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(deletePackage.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.packages = state.packages.filter(
          (pkg) => pkg._id !== action.payload.id
        );
      })
      .addCase(deletePackage.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload;
      })
      .addCase(togglePackageFeaturedStatus.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(togglePackageFeaturedStatus.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.package = action.payload;
      })
      .addCase(togglePackageFeaturedStatus.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload;
      });
  },
});

// Export resetState action
export const { resetState } = packageSlice.actions;

export default packageSlice.reducer;
